import { IGameState } from '../IGameState';
import IPlayerData from '../../../Player/IPlayerData';
import { AsyncActionStatus } from '../../AsyncAction';
import { ThunkAction } from 'redux-thunk';
import { Action } from 'typesafe-actions';
import { DataConflictActions } from '../DataConflictActions';
import { fetchDataConflicts } from '@platform/Utility/SDK/DataConflict/fetchDataConflicts';
import {
    getFacebookData,
    getHighestLevelCompleted,
    hasValidLevelData,
    IPlayerDataLocation,
    hasValidFacebookData,
    PlayerDataLocation,
    sortDataLocationSets,
} from '../../../Utility/DataConflictsHelper';
import { PopupID } from '../../UI/PopupID';
import Game from '../../../Game';
import GameStateHelper from '../GameStateHelper';
import UIStateHelper from 'State/UI/UIStateHelper';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { IRewardData } from 'Utility/RewardHelper';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';

export function fetchConflicts(): ThunkAction<Promise<void>, IGameState, never, Action> {
    return async (dispatch, getState) => {
        const { status } = getState().dataConflict;
        if (status !== AsyncActionStatus.COMPLETE && status !== AsyncActionStatus.EMPTY) {
            return;
        }
        DataConflictActions.startFetchingConflicts();

        const dataSets = await fetchDataConflicts();
        if(!dataSets) {
            DataConflictActions.failFetchingPlayerData();
            return;
        }

        DataConflictActions.setConflicts(dataSets);

    };
}

export function startFetching(state: IGameState): IGameState {
    return {
        ...state,
        dataConflict: {
            ...state.dataConflict,
            status: AsyncActionStatus.QUEUED,
        },
    };
}

export function setConflicts(state: IGameState, dataSets: IPlayerDataLocation[]): IGameState {
    if (dataSets.length > 1) {
        // Has the facebook data progressed passed the current level pack.
        const facebookData = getFacebookData(dataSets);

        if (facebookData && hasValidLevelData(facebookData)) {
            const completedLevel = getHighestLevelCompleted(facebookData);
            if (
                !WorldDataHelper.canGetWorldLevelLimits(WorldDataHelper.maxWorld()) ||
                completedLevel > WorldDataHelper.getWorldLevelLimits(WorldDataHelper.maxWorld()).max
            ) {
                const gameLevelLimit = WorldDataHelper.getWorldLevelLimits(WorldDataHelper.maxWorld()).max;

                return {
                    ...state,
                    gamePaused: true,
                    UIState: UIStateHelper.showPopup(state.UIState, PopupID.PROGRESSION_PASSED_MOBILE_VERSION, {
                        completedLevel,
                        gameLevelLimit,
                    }),
                };
            }
        }

        if (state.playerState.levels.core.length <= 1  && hasValidFacebookData(state, dataSets)) {
            // If signed into Facebook from fresh install don't show data conflicts screen.
            // Resolve game with Facebook data.
            const facebookData = dataSets.filter(dataSets => dataSets.location === PlayerDataLocation.FACEBOOK)[0];
            return resolveConflicts(state, facebookData.playerData);
        }
        return {
            ...state,
            // Show conflict resolution popup
            UIState: UIStateHelper.showPopup(state.UIState, PopupID.DATA_CONFLICT_POPUP, {
                dataSets: sortDataLocationSets(dataSets),
            }),
            // Start conflict resolution
            playerState: {
                ...state.playerState,
            },
            dataConflict: {
                status: AsyncActionStatus.IN_PROGRESS,
                result: sortDataLocationSets(dataSets),
            },
        };
    }
    state = completeFacebookReward(state);
    state.gameLoaded = true;
    // Complete conflict resolution if no conflicts
    return completeConflictResolution(state);
}

export function resolveConflicts(state: IGameState, result: IPlayerData) {
    const previousMaxWorld = state.playerState.maxWorld;
    // Hide conflict resolution popup
    state.UIState = UIStateHelper.hidePopup(state.UIState, PopupID.DATA_CONFLICT_POPUP, {});

    state.playerState = Game.player.data.restorePlayerStateFromEndpointData(result);
    // Reload world.
    const playerLevel = state.playerState.levels.core.length;
    const maxWorld = WorldDataHelper.getWorldNumber(playerLevel);
    if (maxWorld > previousMaxWorld) {
        while (state.playerState.maxWorld < maxWorld) {
            state.playerState = ProgressStateHelper.unlockNextWorld(state.playerState);
        }
    } else {
        state.playerState.maxWorld = maxWorld;
    }

    state = GameStateHelper.loadWorld(state, {
        worldNumber: maxWorld,
    });

    // Complete conflict resolution
    state = completeConflictResolution(state);
    state = completeFacebookReward(state);

    return state;
}

export function failedFetchingPlayerData(state: IGameState): IGameState  {
    return {
        ...state,
        dataConflict: {
            status: AsyncActionStatus.FAILED,
            result: [],
        },
        UIState: UIStateHelper.showPopup(state.UIState, PopupID.ERROR_POPUP, {
            title: Game.locale.getText('ConnectionError'),
            body: Game.locale.getText('CantFetchFacebookData'),
            disallowClose: true,
            submitAction: ()=> DataConflictActions.acceptFailFetchingPlayerData()
        }),
    };
}

export function acceptFailedFetchingConflicts(state: IGameState): IGameState  {
    return {
        ...state,
        gameLoaded: true,
        UIState: UIStateHelper.hidePopup(state.UIState, PopupID.ERROR_POPUP),
    };
}

function completeConflictResolution(state: IGameState): IGameState {
    return {
        ...state,
        playerState: {
            ...state.playerState,
        },
        dataConflict: {
            status: AsyncActionStatus.COMPLETE,
            result: [],
        },
    };
}

function completeFacebookReward(state: IGameState): IGameState {
    const rewards: IRewardData[] = state.platform.facebookSignInReward.result;
    return {
        ...state,
        playerState: InventoryStateHelper.receiveRewards(state.playerState, rewards, {
            dontTrack: true,
            queueTask: true,
        }),
        platform: {
            ...state.platform,
            facebookSignInReward: {
                status: AsyncActionStatus.COMPLETE,
                result: [],
            },
        },
    };
}
